import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../helpers/notificationsApi'
import type { Notification } from '../types/notifications.types'

const QUERY_KEY = ['notifications'] as const

export function useNotifications(enabled = true) {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const canRequestNotifications = enabled && isLoaded && isSignedIn

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchNotifications(apiClient),
    enabled: canRequestNotifications,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(apiClient, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const prev = queryClient.getQueryData<{ notifications: Notification[]; unreadCount: number }>(QUERY_KEY)
      if (prev) {
        queryClient.setQueryData(QUERY_KEY, {
          notifications: prev.notifications.map((n) =>
            n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n,
          ),
          unreadCount: Math.max(0, prev.unreadCount - 1),
        })
      }
      return { prev }
    },
    onError: (_err, _id, context) => {
      if (context?.prev) queryClient.setQueryData(QUERY_KEY, context.prev)
    },
    // No invalidateQueries: cache is updated optimistically; avoid redundant GET /notifications
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(apiClient),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const prev = queryClient.getQueryData<{ notifications: Notification[]; unreadCount: number }>(QUERY_KEY)
      if (prev) {
        queryClient.setQueryData(QUERY_KEY, {
          notifications: prev.notifications.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() })),
          unreadCount: 0,
        })
      }
      return { prev }
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(QUERY_KEY, context.prev)
    },
    // No invalidateQueries: cache is updated optimistically; avoid redundant GET /notifications and slow "read all"
  })

  return {
    notifications: query.data?.notifications ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
    refetch: query.refetch,
  }
}
